import { property, supertypeClass } from '@haventech/amorphic';
import { AnnuityAttributes } from './AnnuityAttributes';
import { Person } from '../../../common/js/customer/Person';
import { CoverpathCustomer } from './CoverpathCustomer';
import { CoverpathPolicy } from './CoverpathPolicy';
import { CoverpathAnnuityApplication } from './CoverpathAnnuityApplication';
import { HitAppCtx, HitAppExecutionCtx } from '../../../common/js/orca/HitAppCtx';
import { WorkflowNames } from '../../../common/js/orca/WorkflowName';
import { Address } from '../../../common/js/customer/Address';
import {
  AnnuityRefundType,
  AnnuityFrequency,
  AnnuityInflationProtector,
  AnnuityLives,
  AnnuitySurvivorshipOption,
  AnnuityReductionAmounts,
  AnnuityProductSelection,
  AnnuityMarket,
  AnnuityGuaranteePeriod,
  AnnuityPeriodCertainOnlyLives,
  AnnuityRenewalOption,
  AnnuityWithdrawalType,
  AnnuityPaymentSource,
} from '../../js/interfaces/CoverpathTypes';
import { Entity } from '../../../common/js/customer/Entity';
import { POLICY_TYPES } from '../../../common/js/constants/policy';
import { AnnuitySourcePayment } from './AnnuitySourcePayment';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class CoverpathAnnuity extends CoverpathPolicy {
  init(customer: CoverpathCustomer, ownerType?: 'person' | 'entity') {
    super.init(customer, POLICY_TYPES.PRIMARY);

    /*
      the base Policy class does the following:
        this.owner = Person.new(this.customer);
        this.trustOrOtherEntity = null;

      That's not what we want if the owner is an entity, so handle that specifically
    */

    if (ownerType && ownerType === 'entity') {
      this.trustOrOtherEntity = Entity.new(customer, false, this);
      this.owner = this.trustOrOtherEntity;
    }

    this.owner.legalAddress = Address.new(customer);

    this.annuityAttributes = AnnuityAttributes.new();
    return this;
  }

  static new(customer: CoverpathCustomer, ownerType?: 'person' | 'entity') {
    return new CoverpathAnnuity().init(customer, ownerType);
  }

  @property({
    comment: 'The ID of the Questionnaire Service suitability form linked to this application',
    toServer: false,
  })
  suitabilityFormQsID: string;

  @property({
    comment: 'The ID of the Questionnaire Service FR3013 form linked to this application',
    toServer: false,
  })
  fR3013FormQsID: string;

  @property({
    comment: 'The ID of the Questionnaire Service replacement form linked to this application',
    toServer: false,
  })
  replacementFormQsID: string;

  @property({
    comment: 'The ID of the Questionnaire Service certificate of trust form linked to this application',
    toServer: false,
  })
  cotFormQsID: string;

  @property({
    comment: 'Related party to the insured that signs on their behalf',
  })
  annuityAttributes: AnnuityAttributes;

  product: AnnuityProductSelection;

  getProductName() {
    return this.getProductInfo().name;
  }

  get annuityApplication(): CoverpathAnnuityApplication {
    return this.application as CoverpathAnnuityApplication;
  }

  get contractNumber(): string {
    return this.policyNumber;
  }

  get annuitant(): Person {
    return this.annuityAttributes.annuitant;
  }

  set annuitant(annuitant: Person) {
    this.annuityAttributes.annuitant = annuitant;
  }

  get jointAnnuitant(): Person {
    return this.annuityAttributes.jointAnnuitant;
  }

  set jointAnnuitant(jointAnnuitant: Person) {
    this.annuityAttributes.jointAnnuitant = jointAnnuitant;
  }

  get initialPurchaseAmount(): number {
    return this.annuityAttributes.initialPurchaseAmount;
  }

  set initialPurchaseAmount(initialPurchaseAmount: number) {
    this.annuityAttributes.initialPurchaseAmount = initialPurchaseAmount;
  }

  get distributingFrequency(): AnnuityFrequency {
    return this.annuityAttributes.distributingFrequency;
  }

  set distributingFrequency(frequency: AnnuityFrequency) {
    this.annuityAttributes.distributingFrequency = frequency;
  }

  get refundType(): AnnuityRefundType {
    return this.annuityAttributes.refundType;
  }

  set refundType(option: AnnuityRefundType) {
    this.annuityAttributes.refundType = option;
  }

  get lives(): AnnuityLives {
    return this.annuityAttributes.lives;
  }

  set lives(option: AnnuityLives) {
    this.annuityAttributes.lives = option;
  }

  get survivorshipOption(): AnnuitySurvivorshipOption {
    return this.annuityAttributes.survivorshipOption;
  }

  set survivorshipOption(option: AnnuitySurvivorshipOption) {
    this.annuityAttributes.survivorshipOption = option;
  }

  get reductionAmount(): AnnuityReductionAmounts {
    return this.annuityAttributes.reductionAmount;
  }

  set reductionAmount(option: AnnuityReductionAmounts) {
    this.annuityAttributes.reductionAmount = option;
  }

  get annuitizationDate(): Date {
    return this.annuityAttributes.annuitizationDate;
  }

  set annuitizationDate(startDate: Date) {
    this.annuityAttributes.annuitizationDate = startDate;
  }

  get recurringPurchaseFrequency(): AnnuityFrequency {
    return this.annuityAttributes.recurringPurchaseFrequency;
  }

  set recurringPurchaseFrequency(frequency: AnnuityFrequency) {
    this.annuityAttributes.recurringPurchaseFrequency = frequency;
  }

  get recurringPurchaseAmount(): number {
    return this.annuityAttributes.recurringPurchaseAmount;
  }

  set recurringPurchaseAmount(amount: number) {
    this.annuityAttributes.recurringPurchaseAmount = amount;
  }

  get initialPayoutAmount(): number {
    return this.annuityAttributes.initialPayoutAmount;
  }

  set initialPayoutAmount(amount: number) {
    this.annuityAttributes.initialPayoutAmount = amount;
  }

  get inflationProtectionRider(): AnnuityInflationProtector {
    return this.annuityAttributes.inflationProtectionRider;
  }

  set inflationProtectionRider(amount: AnnuityInflationProtector) {
    this.annuityAttributes.inflationProtectionRider = amount;
  }

  get returnOfPremium(): boolean {
    return this.annuityAttributes.returnOfPremium;
  }

  set returnOfPremium(returnOfPremium: boolean) {
    this.annuityAttributes.returnOfPremium = returnOfPremium;
  }

  get quoteEffectiveDate(): Date {
    return this.annuityAttributes.quoteEffectiveDate;
  }

  set quoteEffectiveDate(quoteEffectiveDate: Date) {
    this.annuityAttributes.quoteEffectiveDate = quoteEffectiveDate;
  }

  get periodCertainNumYears(): number {
    return this.annuityAttributes.periodCertainNumYears;
  }

  set periodCertainNumYears(amount: number) {
    this.annuityAttributes.periodCertainNumYears = amount;
  }

  get periodCertainNumMonths(): number {
    return this.annuityAttributes.periodCertainNumMonths;
  }

  set periodCertainNumMonths(amount: number) {
    this.annuityAttributes.periodCertainNumMonths = amount;
  }

  get holdFunds(): boolean {
    return this.annuityAttributes.holdFunds;
  }

  set holdFunds(holdFunds: boolean) {
    this.annuityAttributes.holdFunds = holdFunds;
  }

  get guaranteePeriod(): AnnuityGuaranteePeriod {
    return this.annuityAttributes.guaranteePeriod;
  }

  set guaranteePeriod(guaranteePeriod: AnnuityGuaranteePeriod) {
    this.annuityAttributes.guaranteePeriod = guaranteePeriod;
  }

  get marketType(): AnnuityMarket {
    return this.annuityAttributes.marketType;
  }

  set marketType(type: AnnuityMarket) {
    this.annuityAttributes.marketType = type;
  }

  get periodCertainOnlyLives(): AnnuityPeriodCertainOnlyLives {
    return this.annuityAttributes.periodCertainOnlyLives;
  }

  set periodCertainOnlyLives(lives: AnnuityPeriodCertainOnlyLives) {
    this.annuityAttributes.periodCertainOnlyLives = lives;
  }

  get renewalOption(): AnnuityRenewalOption {
    return this.annuityAttributes.renewalOption;
  }

  set renewalOption(renewalOption: AnnuityRenewalOption) {
    this.annuityAttributes.renewalOption = renewalOption;
  }

  get renewalDuration(): number {
    return this.annuityAttributes.renewalDurationInYears;
  }

  set renewalDuration(renewalDuration: number) {
    this.annuityAttributes.renewalDurationInYears = renewalDuration;
  }

  get withdrawalMode(): AnnuityFrequency {
    return this.annuityAttributes.withdrawalMode;
  }

  set withdrawalMode(withdrawalMode: AnnuityFrequency) {
    this.annuityAttributes.withdrawalMode = withdrawalMode;
  }

  get withdrawalType(): AnnuityWithdrawalType {
    return this.annuityAttributes.withdrawalType;
  }

  set withdrawalType(withdrawalType: AnnuityWithdrawalType) {
    this.annuityAttributes.withdrawalType = withdrawalType;
  }

  get withdrawalValue(): number {
    return this.annuityAttributes.withdrawalValue;
  }

  set withdrawalValue(withdrawalValue: number) {
    this.annuityAttributes.withdrawalValue = withdrawalValue;
  }

  get withdrawalStart(): Date {
    return this.annuityAttributes.withdrawalStart;
  }

  set withdrawalStart(withdrawalStart: Date) {
    this.annuityAttributes.withdrawalStart = withdrawalStart;
  }

  get withdrawalEnd(): Date {
    return this.annuityAttributes.withdrawalEnd;
  }

  set withdrawalEnd(withdrawalEnd: Date) {
    this.annuityAttributes.withdrawalEnd = withdrawalEnd;
  }

  get sourcePayments(): Array<AnnuitySourcePayment> {
    return this.annuityAttributes.sourcePayments;
  }

  set sourcePayments(sourcePayments: Array<AnnuitySourcePayment>) {
    this.annuityAttributes.sourcePayments = sourcePayments;
  }

  getPaymentSourceValues(): Array<AnnuityPaymentSource> {
    const sources = [];
    this.sourcePayments.forEach((source) => sources.push(source.source));
    return sources;
  }

  constructor() {
    super();
  }

  /**
   * @override
   */
  getApplicationHierarchy(): string[] {
    return ['HIT', 'Annuity'];
  }

  /**
   * @override
   */
  getApplicationState() {
    return this.owner.legalAddress.state;
  }

  getApplicationCity() {
    return this.owner.legalAddress.city;
  }

  /**
   * @override
   */
  insuredIsJuvenile(): boolean {
    return false;
  }

  /**
   * @override
   */
  insuredIsJuvenileHasSigner(): boolean {
    return false;
  }

  /**
   * @override
   */
  isWholeLifePolicy(): boolean {
    return false;
  }
  /**
   * @override
   */
  isTermLifePolicy(): boolean {
    return false;
  }
  isOwnerSameAsInsured() {
    return false;
  }

  isOwnerPerson(): boolean {
    return this.owner instanceof Person;
  }

  /**
   * @override
   */
  initSecondaryAddresseePerson(customer: CoverpathCustomer) {
    // NOT required for annuity
    // we are overriding it because it breaks quote creation
  }

  async fulfillRequirements(requirements: string[], forceDependent?: boolean) {
    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathAnnuity',
        function: 'fulfillRequirements',
        category: UICategory.request,
        data: {
          'policyNumber': this.contractNumber,
          'requirements': requirements,
          'Future State': this.futureWorkflowState,
          'Future Sub State': this.futureWorkflowSubState,
        },
      },
      `fulfillRequirements publish to Orca`
    );
    //For regression test cases, we will need to set currentTimeOffset in HitAppCtx.newMsgCtx.
    //We didn't setup here because this function is not tested in regression test.

    return HitAppCtx.get.startNewExecution(async (exnCtx) => {
      (exnCtx as HitAppExecutionCtx).workflowName = WorkflowNames.HIT_ANNUITY.name;
      return HitAppCtx.get.policyClient.queueFulfillRequirements(this.application._id, requirements, !!forceDependent);
    }, HitAppCtx.newMsgCtx(`policy_${this.contractNumber}`, this.application._id, 'ApplicationId', 'fulfillReq'));
  }

  getNumDaysToConfirmPolicy(): number {
    return 14;
  }

  getGuaranteePeriodInMonths(): number {
    if (!this.guaranteePeriod) {
      return 0;
    }

    switch (this.guaranteePeriod) {
      case AnnuityGuaranteePeriod.THREE_YEAR:
        return 3 * 12;
      case AnnuityGuaranteePeriod.FOUR_YEAR:
        return 4 * 12;
      case AnnuityGuaranteePeriod.FIVE_YEAR:
        return 5 * 12;
      case AnnuityGuaranteePeriod.SEVEN_YEAR:
        return 7 * 12;
      case AnnuityGuaranteePeriod.NINE_YEAR:
        return 9 * 12;
      default:
        const _exhaustiveCheck: never = this.guaranteePeriod;
        return _exhaustiveCheck;
    }
  }

  getPeriodCertainDurationInMonths(): number {
    //HL-38157 - only applicable when refundType is PeriodCertain,
    //Consider if we should return undefined if refundType is not PeriodCertain
    const years = this.periodCertainNumYears || 0;
    const months = this.periodCertainNumMonths || 0;

    return years * 12 + months;
  }

  isSingleLife(): boolean {
    // Old annuities dont have lives set, so we need to handle that
    return !this.lives || this.lives === AnnuityLives.SINGLE_LIFE;
  }

  isJointLife(): boolean {
    return this.lives === AnnuityLives.JOINT_LIFE;
  }

  isJointLifeConvertible(): boolean {
    return this.lives === AnnuityLives.JOINT_LIFE_CONVERTIBLE;
  }

  isPeriodCertain(): boolean {
    return this.lives === AnnuityLives.PERIOD_CERTAIN_ONLY || this.refundType === AnnuityRefundType.PERIOD_CERTAIN;
  }

  hasCashRefund(): boolean {
    return this.refundType === AnnuityRefundType.CASH;
  }

  hasInstallmentRefund(): boolean {
    return this.refundType === AnnuityRefundType.INSTALLMENT;
  }

  hasPeriodCertainRefund(): boolean {
    return this.refundType === AnnuityRefundType.PERIOD_CERTAIN;
  }

  hasInflationProtector(): boolean {
    return !!this.inflationProtectionRider && this.inflationProtectionRider !== AnnuityInflationProtector.NONE;
  }

  isREC(): boolean {
    return this.product === AnnuityProductSelection.CASREC;
  }

  isRE(): boolean {
    return this.product === AnnuityProductSelection.CASRE;
  }

  isSV(): boolean {
    return this.product === AnnuityProductSelection.CASSV;
  }

  requiresCertificateOfTrust(): boolean {
    return this.cotFormQsID !== null && this.cotFormQsID !== undefined;
  }

  isQlac() {
    return this.isREC() && [AnnuityMarket.CUSTODIAL_QLAC_IRA, AnnuityMarket.QLAC_IRA].includes(this.marketType);
  }

  hasReduction() {
    return (
      this.isJointLife() &&
      (this.refundType === AnnuityRefundType.PERIOD_CERTAIN || this.refundType === AnnuityRefundType.NONE) &&
      !!this.survivorshipOption &&
      this.survivorshipOption !== AnnuitySurvivorshipOption.NO_REDUCTION
    );
  }

  /**
   * Indicates if an Annuity Date Adjustment Rider (ADAR) is needed
   */
  requiresADAR(): boolean {
    if (!this.isREC()) {
      return false;
    }

    const hasAtLeastTenYearPeriodCertainDuration = this.getPeriodCertainDurationInMonths() >= 120;
    return (
      this.hasCashRefund() ||
      this.hasInstallmentRefund() ||
      (this.hasPeriodCertainRefund() && hasAtLeastTenYearPeriodCertainDuration)
    );
  }

  /**
   * @override
   */
  isReplacementPolicy() {
    /*
      On the life side, replacement policy details are on the "existingPolicies" property of insured.
      We don't have that as part of the annuity model, so use the existence of a replacement form to indicate
      if there are replacements.

      This approach might be brittle, but time will tell.
     */
    return !!this.replacementFormQsID;
  }

  requiresMM2Disclosure(): boolean {
    const marketTypeList = [
      AnnuityMarket._401K,
      AnnuityMarket.KEOGH,
      AnnuityMarket.MONEY_PURCHASE_PENSION_PLAN,
      AnnuityMarket.PENSION_PLAN,
      AnnuityMarket.PROFIT_SHARING_PLAN,
      AnnuityMarket.TARGET_BENEFIT_PLAN,
    ];

    return (
      this.marketType === AnnuityMarket.CUSTODIAL_IRA ||
      (this.isSV() && marketTypeList.includes(this.marketType)) ||
      (this.isREC() && this.marketType === AnnuityMarket.CUSTODIAL_QLAC_IRA)
    );
  }

  isNonQualified() {
    return (
      this.marketType === AnnuityMarket.NON_QUALIFIED || this.marketType === AnnuityMarket.NON_QUALIFIED_DEFERRED_COMP
    );
  }

  /**
   * Indicates if an Annuity Large Case Questionnaire is needed
   * Note: Conditions for including this form will change in the future
   */
  requiresLargeCaseQuestionnaire(): boolean {
    return this.initialPurchaseAmount >= 1500000;
  }

  requiresFixedAnnuityDisclosureAcknowledgment(): boolean {
    const marketTypeList = [
      AnnuityMarket.MONEY_PURCHASE_PENSION_PLAN,
      AnnuityMarket._401K,
      AnnuityMarket.KEOGH,
      AnnuityMarket.PENSION_PLAN,
      AnnuityMarket.PROFIT_SHARING_PLAN,
    ];
    const sourcePaymentList = [AnnuityPaymentSource.QUALIFIED_EMPLOYER_PLAN, AnnuityPaymentSource.KEOGH];

    const isSourcePaymentSelected = this.getPaymentSourceValues().some(
      (source) => sourcePaymentList.indexOf(source) >= 0
    );
    return marketTypeList.includes(this.marketType) || isSourcePaymentSelected;
  }
}
